import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

import { SchedulableResourceType } from "@/types/scheduling/schedule";
import scheduleApis from "@/types/scheduling/scheduleApi";
import { TokenGenerate, TokenRead } from "@/types/tokens/token/token";
import { TokenCategoryRead } from "@/types/tokens/tokenCategory/tokenCategory";
import tokenCategoryApis from "@/types/tokens/tokenCategory/tokenCategoryApi";
import { TokenQueueRead } from "@/types/tokens/tokenQueue/tokenQueue";
import tokenQueueApi from "@/types/tokens/tokenQueue/tokenQueueApi";
import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";

const AUTO_QUEUE_VALUE = "__auto__";

interface TokenGenerationSheetProps {
  facilityId: string;
  appointmentId: string;
  resourceType: SchedulableResourceType;
  resourceId: string;
  date: string;
  trigger: React.ReactNode;
  onSuccess?: () => void;
}

export function TokenGenerationSheet({
  facilityId,
  appointmentId,
  resourceType,
  resourceId,
  date,
  trigger,
  onSuccess,
}: TokenGenerationSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedQueue, setSelectedQueue] = useState<string>(AUTO_QUEUE_VALUE);
  const [note, setNote] = useState("");

  // Fetch token categories
  const { data: tokenCategories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["token_categories", facilityId],
    queryFn: query(tokenCategoryApis.list, {
      pathParams: { facility_id: facilityId },
      queryParams: {
        resource_type: resourceType,
      },
    }),
    enabled: isOpen && !!facilityId,
  });

  const { data: tokenQueues, isLoading: isLoadingQueues } = useQuery({
    queryKey: ["token_queues", facilityId, resourceType, resourceId, date],
    queryFn: query(tokenQueueApi.list, {
      pathParams: { facility_id: facilityId },
      queryParams: {
        resource_type: resourceType,
        resource_id: resourceId,
        date,
      },
    }),
    enabled: isOpen && !!facilityId && !!resourceId && !!date,
  });

  // Set default category when sheet opens and categories are available
  useEffect(() => {
    if (
      isOpen &&
      tokenCategories?.results &&
      tokenCategories.results.length > 0
    ) {
      const defaultCategory = tokenCategories.results.find(
        (category) => category.default,
      );
      if (defaultCategory) {
        setSelectedCategory(defaultCategory.id);
      } else {
        // If no default category, reset to empty
        setSelectedCategory("");
      }
      // Always reset note when opening
      setNote("");

      const primaryQueue = tokenQueues?.results?.find(
        (q: TokenQueueRead) => q.is_primary,
      );
      setSelectedQueue(primaryQueue ? primaryQueue.id : AUTO_QUEUE_VALUE);
    }
  }, [isOpen, tokenCategories, tokenQueues]);

  // Generate token mutation
  const { mutate: generateToken, isPending: isGenerating } = useMutation({
    mutationFn: mutate(scheduleApis.appointments.generateToken, {
      pathParams: { facilityId, id: appointmentId },
    }),
    onSuccess: (data: TokenRead) => {
      toast.success(t("token_generated_successfully"));
      setIsOpen(false);
      onSuccess?.();
      // Invalidate appointment data to refresh
      queryClient.invalidateQueries({
        queryKey: ["appointment", appointmentId],
      });
      // Invalidate the token queue board so the generated token shows up and
      // the token numbers keep behaving like a queue
      if (data?.queue?.id) {
        queryClient.invalidateQueries({
          queryKey: ["infinite-tokens", facilityId, data.queue.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["token-queue-summary", facilityId, data.queue.id],
        });
      }
    },
  });

  const handleGenerateToken = () => {
    if (!selectedCategory) {
      toast.error(t("please_fill_all_fields"));
      return;
    }

    const tokenData: TokenGenerate & { queue?: string } = {
      category: selectedCategory,
      note: note.trim(),
      queue: selectedQueue === AUTO_QUEUE_VALUE ? undefined : selectedQueue,
    };

    generateToken(tokenData);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedCategory("");
      setSelectedQueue(AUTO_QUEUE_VALUE);
      setNote("");
    }
  };

  const hasPrimaryQueue = tokenQueues?.results?.some(
    (q: TokenQueueRead) => q.is_primary,
  );

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t("generate_token")}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isLoadingCategories ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-medium">
                  {t("token_category")} *
                </Label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("select_token_category")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tokenCategories?.results?.map(
                      (category: TokenCategoryRead) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                          {category.default && ` (${t("default")})`}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              {(tokenQueues?.results?.length ?? 0) > 1 && (
                <div className="space-y-2">
                  <Label htmlFor="queue" className="text-sm font-medium">
                    {t("queue")}
                  </Label>
                  <Select
                    value={selectedQueue}
                    onValueChange={setSelectedQueue}
                    disabled={isLoadingQueues}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_token_queue")} />
                    </SelectTrigger>
                    <SelectContent>
                      {!hasPrimaryQueue && (
                        <SelectItem value={AUTO_QUEUE_VALUE}>
                          {t("create_a_queue", "Create a queue")}
                        </SelectItem>
                      )}
                      {tokenQueues?.results?.map((queue: TokenQueueRead) => (
                        <SelectItem key={queue.id} value={queue.id}>
                          {queue.name}
                          {queue.is_primary && ` (${t("primary")})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="note" className="text-sm font-medium">
                  {t("note")}
                </Label>
                <Textarea
                  id="note"
                  placeholder={t("enter_token_note")}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="flex-1"
                >
                  <ShortcutBadge actionId="cancel-action" />
                  {t("cancel")}
                </Button>
                <Button
                  onClick={handleGenerateToken}
                  disabled={!selectedCategory || isGenerating}
                  className="flex-1"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      {t("generating")}
                    </>
                  ) : (
                    <>
                      {t("generate_token")}
                      <ShortcutBadge actionId="submit-action" />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
